import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import DeviceSearchAutocomplete from "../../Components/DeviceSearchAutocomplete";
import DialogAppBar from "../../Components/DialogAppBar";
import SerialisedSubAssemblySearchAutocomplete from "../../Components/SerialisedSubAssemblySearchAutocomplete";
import Title from "../../Components/Title";
import {
  formatGoogleDriveMediaLink,
  getOrderSerialAllocationColor,
  getToken,
  requestErrorHandler,
} from "../../Utils/Common";
import EnvSettings from "../../Utils/EnvSettings";
import { showPopupSnackbar } from "../../redux/actions/snackbarActions";

const server = EnvSettings.server;

const createDefaultSerialisedSubassembly = () => ({
  number: "",
  model: "",
  color: "",
});

const createDefaultBatchDetails = () => ({
  number: "",
  size: 1,
});

const incrementSerialDigits = (value) => {
  if (typeof value !== "string" || !value.length) {
    return null;
  }
  const digits = value.split("");
  let carry = 1;
  for (let index = digits.length - 1; index >= 0; index -= 1) {
    const digit = Number(digits[index]);
    if (Number.isNaN(digit)) {
      return null;
    }
    const sum = digit + carry;
    digits[index] = String(sum % 10);
    carry = Math.floor(sum / 10);
    if (!carry) {
      break;
    }
  }
  while (carry > 0) {
    digits.unshift(String(carry % 10));
    carry = Math.floor(carry / 10);
  }
  return digits.join("");
};

const generateSerialSequence = (startNumber, count) => {
  if (typeof startNumber !== "string" || !startNumber.trim() || count <= 0) {
    return [];
  }
  const trimmed = startNumber.trim();
  const match = trimmed.match(/^(.*?)(\d+)$/);
  let prefix = "";
  let numericPart = "";
  if (match) {
    prefix = match[1];
    numericPart = match[2];
  } else if (/^\d+$/.test(trimmed)) {
    numericPart = trimmed;
  } else {
    return null;
  }
  let current = numericPart;
  const width = numericPart.length;
  const results = [];
  for (let index = 0; index < count; index += 1) {
    const padded =
      current.length < width ? current.padStart(width, "0") : current;
    results.push(`${prefix}${padded}`);
    current = incrementSerialDigits(current);
    if (!current) {
      return null;
    }
  }
  return results;
};

const formatCost = (value) =>
  Number.isFinite(Number(value)) ? Number(value).toFixed(2) : null;

const normalizeDeviceSelection = (selection) => {
  if (!selection) {
    return null;
  }
  if (typeof selection === "string") {
    const serialNumber = selection.replace(/^Add Device\s+/i, "").trim();
    return serialNumber ? { serialNumber } : null;
  }
  const serialNumber =
    selection.serialNumber ||
    (typeof selection.inputValue === "string"
      ? selection.inputValue.replace(/^Add Device\s+/i, "")
      : "");
  const trimmedSerialNumber =
    typeof serialNumber === "string" ? serialNumber.trim() : serialNumber;
  if (!trimmedSerialNumber) {
    return null;
  }
  return {
    id: selection.id || null,
    serialNumber: trimmedSerialNumber,
  };
};

const getComponentSelectionKey = (component) =>
  component?.id
    ? `recipe:${component.id}`
    : `stock:${component?.StockItem?.id}`;

const getRequiredSerialisedSubAssemblyCount = (component) => {
  const quantity = Number(component?.quantity);
  return Number.isInteger(quantity) && quantity > 0 ? quantity : 0;
};

const SERIALISED_SUB_ASSEMBLY_PATTERNS = [
  /(^|[^a-z0-9])ssa([^a-z0-9]|$)/i,
  /\bseriali[sz]ed[-_\s]*sub[-_\s]*assembl(?:y|ies)\b/i,
  /\bsub[-_\s]*assembl(?:y|ies)\b/i,
  /(^|[^a-z0-9])chassis([^a-z0-9]|$)/i,
];

const getStockItemGroupNames = (stockItem) =>
  (stockItem?.StockItemGroups || [])
    .map((group) => group?.StockItemGroupName?.name || group?.name)
    .filter(Boolean);

const matchesSerialisedSubAssemblyDescriptor = (value) =>
  typeof value === "string" &&
  SERIALISED_SUB_ASSEMBLY_PATTERNS.some((pattern) => pattern.test(value));

const isSerialisedSubAssemblyStockItem = (stockItem) =>
  [stockItem?.sku, stockItem?.name, ...getStockItemGroupNames(stockItem)].some(
    matchesSerialisedSubAssemblyDescriptor,
  );

const matchesChassisDescriptor = (value) =>
  typeof value === "string" &&
  /(^|[^a-z0-9])chassis([^a-z0-9]|$)/i.test(value);

const isChassisStockItem = (stockItem) =>
  [stockItem?.sku, stockItem?.name, ...getStockItemGroupNames(stockItem)].some(
    matchesChassisDescriptor,
  );

export default function ManufacturingEntry({
  showTicketEntry: show,
  setShowTicketEntry: hide,
  warehouses = [],
  onEntryCreated,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [manufacturingRecipes, setManufacturingRecipes] = useState([]);
  const [manufacturingFeatureLists, setManufacturingFeatureLists] = useState(
    [],
  );
  const [isFetchingManufacturingData, setIsFetchingManufacturingData] =
    useState(false);
  const [manufacturingDataError, setManufacturingDataError] = useState("");
  const [selectedRecipeId, setSelectedRecipeId] = useState(null);
  const [editableComponents, setEditableComponents] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [customFeatures, setCustomFeatures] = useState([]);
  const [newFeatureName, setNewFeatureName] = useState("");
  const [newFeatureDescription, setNewFeatureDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [featureListFilter, setFeatureListFilter] = useState(null);
  const [createSerialisedSubassembly, setCreateSerialisedSubassembly] =
    useState(false);
  const [serialisedSubassembly, setSerialisedSubassembly] = useState(
    createDefaultSerialisedSubassembly,
  );
  const [linkCompletedDevice, setLinkCompletedDevice] = useState(false);
  const [producedDevice, setProducedDevice] = useState(null);
  const [consumedSerialisedSubAssemblies, setConsumedSerialisedSubAssemblies] =
    useState({});
  const [batchDetails, setBatchDetails] = useState(createDefaultBatchDetails);
  const warehouseOptions = useMemo(
    () =>
      (warehouses || [])
        .map((warehouse) => ({
          id: warehouse.id ?? warehouse.value ?? null,
          label: warehouse.label || warehouse.name || warehouse.value || "",
          name: warehouse.name || "",
          value: warehouse.value || "",
        }))
        .filter((option) => option.id),
    [warehouses],
  );
  const [selectedWarehouseId, setSelectedWarehouseId] = useState(null);
  const hasSelectedWarehouse = Boolean(selectedWarehouseId);
  const [stockQuantitiesByItemId, setStockQuantitiesByItemId] = useState({});
  const selectedWarehouseOption = useMemo(() => {
    if (!selectedWarehouseId) {
      return null;
    }
    return warehouseOptions.find(
      (warehouse) =>
        String(warehouse?.id ?? "") === String(selectedWarehouseId ?? ""),
    );
  }, [selectedWarehouseId, warehouseOptions]);
  const selectedWarehouseName = selectedWarehouseOption?.name || "";
  const selectedWarehouseSerialisedLocation =
    selectedWarehouseOption?.value ||
    selectedWarehouseOption?.name ||
    selectedWarehouseOption?.label ||
    "";
  const parsedBatchSize = Math.max(
    0,
    Math.floor(Number(batchDetails.size) || 0),
  );
  const hasSerialNumber = Boolean(serialisedSubassembly.number?.trim());

  useEffect(() => {
    let isMounted = true;
    setIsFetchingManufacturingData(true);
    setManufacturingDataError("");
    fetch(server + "/get_manufacturing_recipes", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        if (!isMounted) {
          return;
        }
        requestErrorHandler(result, dispatch);
        if (result.error) {
          setManufacturingRecipes([]);
          setManufacturingFeatureLists([]);
          setManufacturingDataError(
            result.message ||
              t(
                "manufacturing.recipesFetchFailed",
                "Failed to fetch manufacturing recipes.",
              ),
          );
        } else {
          setManufacturingRecipes(
            Array.isArray(result.manufacturingRecipes)
              ? result.manufacturingRecipes
              : [],
          );
          setManufacturingFeatureLists(
            Array.isArray(result.manufacturingFeatureLists)
              ? result.manufacturingFeatureLists
              : [],
          );
        }
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }
        setManufacturingRecipes([]);
        setManufacturingFeatureLists([]);
        setManufacturingDataError(
          t(
            "manufacturing.recipesFetchFailed",
            "Failed to fetch manufacturing recipes.",
          ),
        );
      })
      .finally(() => {
        if (isMounted) {
          setIsFetchingManufacturingData(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [dispatch, t]);

  useEffect(() => {
    if (!manufacturingRecipes.length) {
      if (selectedRecipeId !== null) {
        setSelectedRecipeId(null);
      }
      return;
    }
    if (
      selectedRecipeId &&
      manufacturingRecipes.some((recipe) => recipe.id === selectedRecipeId)
    ) {
      return;
    }
  }, [manufacturingRecipes, selectedRecipeId]);

  const selectedRecipe = useMemo(
    () =>
      manufacturingRecipes.find((recipe) => recipe.id === selectedRecipeId) ??
      null,
    [manufacturingRecipes, selectedRecipeId],
  );

  const selectedRecipeServiceItems = useMemo(
    () =>
      (selectedRecipe?.ManufacturingRecipeServiceItems || [])
        .map((entry) => entry.ServiceItem)
        .filter(Boolean),
    [selectedRecipe],
  );

  const hasSelectedRecipe = Boolean(selectedRecipe);

  useEffect(() => {
    if (!selectedRecipe) {
      setEditableComponents([]);
      setSelectedFeatures([]);
      setSerialisedSubassembly({ number: "", model: "", color: "" });
      setLinkCompletedDevice(false);
      setProducedDevice(null);
      setConsumedSerialisedSubAssemblies({});
      return;
    }
    setEditableComponents(
      (selectedRecipe.ManufacturingRecipeStockItems ?? []).map((component) => ({
        id: component.id,
        StockItem: component.StockItem,
        quantity: component.quantity ?? 1,
        requiresSerialisedSubAssembly: Boolean(
          component.requiresSerialisedSubAssembly ||
          isSerialisedSubAssemblyStockItem(component.StockItem),
        ),
      })),
    );
    setSelectedFeatures([]);
    setLinkCompletedDevice(false);
    setProducedDevice(null);
    setConsumedSerialisedSubAssemblies({});
    setSerialisedSubassembly((prev) => ({
      ...prev,
      number:
        selectedRecipe.nextSerialNumber?.toString() ??
        selectedRecipe.startingSerialNumber?.toString() ??
        "",
    }));
  }, [selectedRecipe]);

  const recipeFilterOptions = useMemo(
    () =>
      createFilterOptions({
        stringify: (option) =>
          `${option?.name ?? ""} ${option?.StockItem?.sku ?? ""}`,
      }),
    [],
  );

  const rawFeatureListOptions = useMemo(() => {
    const grouped = new Map();
    manufacturingFeatureLists.forEach((entry) => {
      const listName = entry.ManufacturingFeatureListName;
      if (!listName) {
        return;
      }
      const existing = grouped.get(listName.id);
      const feature = entry.ManufacturingFeature;
      if (!feature) {
        return;
      }
      const featureData = {
        id: feature.id,
        name: feature.name,
        description: feature.description,
        stockItemId: feature.StockItem?.id ?? null,
        sku: feature.StockItem?.sku,
        featureListName: listName,
      };
      if (existing) {
        existing.features.push(featureData);
      } else {
        grouped.set(listName.id, {
          ...listName,
          features: [featureData],
        });
      }
    });
    return Array.from(grouped.values());
  }, [manufacturingFeatureLists]);

  const featureListOptions = useMemo(() => {
    if (!selectedRecipe?.StockItem?.id) {
      return [];
    }
    const recipeStockItemId = selectedRecipe.StockItem.id;
    return rawFeatureListOptions.filter(
      (option) => option.StockItem?.id === recipeStockItemId,
    );
  }, [rawFeatureListOptions, selectedRecipe]);

  useEffect(() => {
    if (!featureListFilter) {
      return;
    }
    const refreshedOption = featureListOptions.find(
      (option) => option.id === featureListFilter.id,
    );
    if (!refreshedOption) {
      setFeatureListFilter(null);
    } else if (refreshedOption !== featureListFilter) {
      setFeatureListFilter(refreshedOption);
    }
  }, [featureListFilter, featureListOptions]);

  useEffect(() => {
    if (!featureListOptions.length) {
      setSelectedFeatures((prev) => (prev.length ? [] : prev));
      return;
    }
    const allowedIds = new Set();
    featureListOptions.forEach((option) => {
      option.features.forEach((feature) => {
        if (feature.id) {
          allowedIds.add(feature.id);
        }
      });
    });
    setSelectedFeatures((prev) => {
      if (!prev.length) {
        return prev;
      }
      const filtered = prev.filter(
        (feature) => !feature?.id || allowedIds.has(feature.id),
      );
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [featureListOptions]);

  useEffect(() => {
    if (!warehouseOptions.length) {
      if (selectedWarehouseId !== null) {
        setSelectedWarehouseId(null);
      }
      return;
    }
    if (!warehouseOptions.some((option) => option.id === selectedWarehouseId)) {
      setSelectedWarehouseId(null);
    }
  }, [warehouseOptions, selectedWarehouseId]);

  const resetForm = useCallback(() => {
    setSelectedRecipeId(null);
    setEditableComponents([]);
    setSelectedFeatures([]);
    setCustomFeatures([]);
    setNewFeatureName("");
    setNewFeatureDescription("");
    setFeatureListFilter(null);
    setCreateSerialisedSubassembly(false);
    setSerialisedSubassembly(createDefaultSerialisedSubassembly());
    setLinkCompletedDevice(false);
    setProducedDevice(null);
    setConsumedSerialisedSubAssemblies({});
    setBatchDetails(createDefaultBatchDetails());
    setSelectedWarehouseId(null);
  }, []);

  const exit = useCallback(() => {
    resetForm();
    hide(false);
  }, [hide, resetForm]);

  useEffect(() => {
    if (!show) {
      resetForm();
    }
  }, [resetForm, show]);

  const componentCount = (recipe) =>
    recipe?.ManufacturingRecipeStockItems?.length ?? 0;

  useEffect(() => {
    if (!selectedWarehouseId) {
      setStockQuantitiesByItemId({});
      return;
    }
    if (!selectedWarehouseName) {
      setStockQuantitiesByItemId({});
      return;
    }
    const stockItemIds = editableComponents
      .map((component) => component?.StockItem?.id)
      .filter(Boolean);
    if (!stockItemIds.length) {
      setStockQuantitiesByItemId({});
      return;
    }
    let isActive = true;
    Promise.all(
      stockItemIds.map((stockItemId) =>
        fetch(server + "/get_stock_quantity", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token: getToken(),
            search_column: "StockItemId",
            search_value: stockItemId,
            warehouse: selectedWarehouseName,
          }),
        })
          .then((res) => res.json())
          .then((result) => {
            if (result?.error || !result?.stock) {
              return null;
            }
            return {
              stockItemId,
              total: Number(result.stock.total),
              free: Number(result.stock.free),
            };
          })
          .catch(() => null),
      ),
    ).then((results) => {
      if (!isActive) {
        return;
      }
      const nextMap = {};
      results.filter(Boolean).forEach((entry) => {
        nextMap[entry.stockItemId] = {
          total: Number.isFinite(entry.total) ? entry.total : null,
          free: Number.isFinite(entry.free) ? entry.free : null,
        };
      });
      setStockQuantitiesByItemId(nextMap);
    });
    return () => {
      isActive = false;
    };
  }, [editableComponents, selectedWarehouseId, selectedWarehouseName]);

  const bomRows = useMemo(() => {
    if (!selectedRecipe) {
      return [];
    }
    const effectiveBatchSize = parsedBatchSize > 0 ? parsedBatchSize : 1;
    const componentEntries = editableComponents.map((component) => ({
      key: `component-${component.id}`,
      type: "component",
      sku: component.StockItem?.sku || "",
      name: component.StockItem?.name || "",
      quantity: Number(component.quantity) || 0,
      stockProjection: (() => {
        const stockItemId = component.StockItem?.id;
        if (!selectedWarehouseId || !stockItemId) {
          return null;
        }
        const currentTotal =
          stockQuantitiesByItemId[stockItemId]?.total ??
          stockQuantitiesByItemId[stockItemId]?.free ??
          null;
        if (currentTotal === null || currentTotal === undefined) {
          return null;
        }
        const usage = (Number(component.quantity) || 0) * effectiveBatchSize;
        const projectedTotal = Number(currentTotal) - usage;
        return `${currentTotal} → ${projectedTotal}`;
      })(),
      currentTotal: (() => {
        const stockItemId = component.StockItem?.id;
        if (!selectedWarehouseId || !stockItemId) {
          return null;
        }
        const currentTotal =
          stockQuantitiesByItemId[stockItemId]?.total ??
          stockQuantitiesByItemId[stockItemId]?.free ??
          null;
        return currentTotal ?? null;
      })(),
      projectedTotal: (() => {
        const stockItemId = component.StockItem?.id;
        if (!selectedWarehouseId || !stockItemId) {
          return null;
        }
        const currentTotal =
          stockQuantitiesByItemId[stockItemId]?.total ??
          stockQuantitiesByItemId[stockItemId]?.free ??
          null;
        if (currentTotal === null || currentTotal === undefined) {
          return null;
        }
        const usage = (Number(component.quantity) || 0) * effectiveBatchSize;
        return Number(currentTotal) - usage;
      })(),
      cost:
        component.StockItem?.costPrice !== undefined &&
        component.StockItem?.costPrice !== null
          ? (Number(component.StockItem.costPrice) || 0) *
            (Number(component.quantity) || 0)
          : null,
    }));
    const serviceEntries = selectedRecipeServiceItems.map((service) => ({
      key: `service-${service.id}`,
      type: "service",
      sku: service.sku || "",
      name: service.name || "",
      quantity: null,
      stockProjection: null,
      currentTotal: null,
      projectedTotal: null,
      cost:
        service.costPrice !== undefined && service.costPrice !== null
          ? Number(service.costPrice)
          : null,
    }));
    return [...componentEntries, ...serviceEntries];
  }, [
    editableComponents,
    parsedBatchSize,
    selectedRecipe,
    selectedRecipeServiceItems,
    selectedWarehouseId,
    stockQuantitiesByItemId,
  ]);

  const bomCostSummary = useMemo(() => {
    const costs = bomRows
      .map((row) => Number(row.cost))
      .filter((value) => Number.isFinite(value));
    const perUnitTotal = costs.reduce((sum, value) => sum + value, 0);
    const batchMultiplier = parsedBatchSize > 0 ? parsedBatchSize : 1;
    return {
      total: perUnitTotal * batchMultiplier,
      perUnitTotal,
      hasCost: costs.length > 0,
    };
  }, [bomRows, parsedBatchSize]);

  const selectedResultingDeviceProfileId =
    selectedRecipe?.StockItem?.DeviceProfileId ||
    selectedRecipe?.StockItem?.DeviceProfile?.id ||
    null;
  const selectedResultingStockItemColor = getOrderSerialAllocationColor(
    selectedRecipe?.StockItem,
  );
  const producedDeviceSerialNumber =
    typeof producedDevice?.serialNumber === "string"
      ? producedDevice.serialNumber.trim()
      : "";
  const serialisedComponentRequirements = useMemo(
    () =>
      linkCompletedDevice
        ? editableComponents.filter(
            (component) => component.requiresSerialisedSubAssembly,
          )
        : [],
    [editableComponents, linkCompletedDevice],
  );

  useEffect(() => {
    setConsumedSerialisedSubAssemblies((prev) => {
      const next = {};
      editableComponents.forEach((component) => {
        if (!component.requiresSerialisedSubAssembly) {
          return;
        }
        const key = getComponentSelectionKey(component);
        const requiredCount = getRequiredSerialisedSubAssemblyCount(component);
        const previousSelections = Array.isArray(prev[key]) ? prev[key] : [];
        next[key] = previousSelections.slice(0, requiredCount);
        while (next[key].length < requiredCount) {
          next[key].push(null);
        }
      });
      return next;
    });
  }, [editableComponents]);

  useEffect(() => {
    if (!linkCompletedDevice) {
      return;
    }
    setBatchDetails((prev) =>
      Number(prev.size) === 1 ? prev : { ...prev, size: 1 },
    );
  }, [linkCompletedDevice]);

  const selectedConsumedSerialisedSubAssemblyIds = useMemo(
    () =>
      Object.values(consumedSerialisedSubAssemblies)
        .flat()
        .filter(Boolean)
        .map((subAssembly) => subAssembly.id)
        .filter(Boolean),
    [consumedSerialisedSubAssemblies],
  );

  const hasInvalidSerialisedSubAssemblyQuantity =
    linkCompletedDevice &&
    serialisedComponentRequirements.some(
      (component) => getRequiredSerialisedSubAssemblyCount(component) <= 0,
    );

  const missingSerialisedSubAssemblySelections =
    linkCompletedDevice &&
    serialisedComponentRequirements.some((component) => {
      const key = getComponentSelectionKey(component);
      const requiredCount = getRequiredSerialisedSubAssemblyCount(component);
      const selectedCount = (consumedSerialisedSubAssemblies[key] || []).filter(
        Boolean,
      ).length;
      return selectedCount !== requiredCount;
    });

  const consumedSerialisedSubAssembliesPayload = useMemo(() => {
    if (!linkCompletedDevice) {
      return [];
    }
    return editableComponents.flatMap((component) => {
      const key = getComponentSelectionKey(component);
      return (consumedSerialisedSubAssemblies[key] || [])
        .filter(Boolean)
        .map((subAssembly) => ({
          manufacturingRecipeStockItemId: component.id,
          stockItemId: component.StockItem?.id,
          serialisedSubAssemblyId: subAssembly.id,
        }));
    });
  }, [
    consumedSerialisedSubAssemblies,
    editableComponents,
    linkCompletedDevice,
  ]);

  const recipeStockItemsPayload = useMemo(
    () =>
      editableComponents
        .filter((component) => component?.StockItem?.id)
        .map((component) => ({
          manufacturingRecipeStockItemId: component.id,
          stockItemId: component.StockItem.id,
          quantity: Number(component.quantity) || 0,
        })),
    [editableComponents],
  );

  const hasInvalidQuantities = recipeStockItemsPayload.some(
    (component) => component.quantity <= 0,
  );

  const serialPreview = useMemo(() => {
    if (!createSerialisedSubassembly) {
      return { numbers: [], error: false };
    }
    const baseNumber = serialisedSubassembly.number?.trim();
    if (!baseNumber || parsedBatchSize <= 1) {
      return { numbers: [], error: false };
    }
    const sequence = generateSerialSequence(baseNumber, parsedBatchSize);
    if (!sequence) {
      return { numbers: [], error: true };
    }
    return { numbers: sequence, error: false };
  }, [
    createSerialisedSubassembly,
    parsedBatchSize,
    serialisedSubassembly.number,
  ]);

  const disableSubmit =
    !selectedRecipe ||
    hasInvalidQuantities ||
    !selectedWarehouseId ||
    parsedBatchSize <= 0 ||
    (linkCompletedDevice && !producedDeviceSerialNumber) ||
    hasInvalidSerialisedSubAssemblyQuantity ||
    missingSerialisedSubAssemblySelections ||
    (!linkCompletedDevice &&
      !createSerialisedSubassembly &&
      !batchDetails.number.trim()) ||
    (createSerialisedSubassembly && !hasSerialNumber) ||
    (createSerialisedSubassembly &&
      parsedBatchSize > 1 &&
      serialPreview.error) ||
    isSubmitting;

  const toggleFeatureSelection = (feature) => {
    if (!feature?.id) {
      return;
    }
    setSelectedFeatures((prev) => {
      if (prev.some((selected) => selected.id === feature.id)) {
        return prev.filter((selected) => selected.id !== feature.id);
      }
      return [...prev, feature];
    });
  };

  const handleProducedDeviceChange = (event, value) => {
    const selection =
      value !== undefined ? value : (event?.target?.value ?? event ?? null);
    setProducedDevice(normalizeDeviceSelection(selection));
  };

  const updateConsumedSerialisedSubAssembly = (
    component,
    index,
    subAssembly,
  ) => {
    const key = getComponentSelectionKey(component);
    const requiredCount = getRequiredSerialisedSubAssemblyCount(component);
    setConsumedSerialisedSubAssemblies((prev) => {
      const nextSelections = Array.isArray(prev[key])
        ? [...prev[key]]
        : Array(requiredCount).fill(null);
      while (nextSelections.length < requiredCount) {
        nextSelections.push(null);
      }
      nextSelections[index] = subAssembly || null;
      return {
        ...prev,
        [key]: nextSelections.slice(0, requiredCount),
      };
    });
  };

  const handleSubmit = () => {
    if (!selectedRecipe) {
      dispatch(
        showPopupSnackbar({
          message: t(
            "manufacturing.selectRecipeFirst",
            "Select a recipe before creating an entry.",
          ),
          severity: "warning",
        }),
      );
      return;
    }
    if (hasInvalidQuantities) {
      dispatch(
        showPopupSnackbar({
          message: t(
            "manufacturing.invalidQuantities",
            "All component quantities must be greater than zero.",
          ),
          severity: "warning",
        }),
      );
      return;
    }
    if (!selectedWarehouseId) {
      dispatch(
        showPopupSnackbar({
          message: t(
            "manufacturing.selectWarehouseFirst",
            "Select a warehouse before creating an entry.",
          ),
          severity: "warning",
        }),
      );
      return;
    }
    if (linkCompletedDevice && !producedDeviceSerialNumber) {
      dispatch(
        showPopupSnackbar({
          message: t(
            "manufacturing.deviceSerialRequired",
            "Select or enter the produced device serial number.",
          ),
          severity: "warning",
        }),
      );
      return;
    }
    if (
      hasInvalidSerialisedSubAssemblyQuantity ||
      missingSerialisedSubAssemblySelections
    ) {
      dispatch(
        showPopupSnackbar({
          message: t(
            "manufacturing.serialisedSubAssemblySelectionRequired",
            "Select the required serialised sub-assembly numbers for this recipe.",
          ),
          severity: "warning",
        }),
      );
      return;
    }
    const submissionBatchSize = linkCompletedDevice
      ? 1
      : createSerialisedSubassembly
        ? Math.max(parsedBatchSize, 1)
        : parsedBatchSize;
    setIsSubmitting(true);
    const sanitizeFeature = (feature) => {
      if (!feature) {
        return null;
      }
      const name = typeof feature.name === "string" ? feature.name.trim() : "";
      if (!name) {
        return null;
      }
      const descriptionSource =
        typeof feature.description === "string"
          ? feature.description
          : feature.featureListName?.name;
      return {
        name,
        description:
          typeof descriptionSource === "string"
            ? descriptionSource.trim() || null
            : null,
      };
    };

    const featurePayload = [
      ...selectedFeatures.map(sanitizeFeature).filter(Boolean),
      ...customFeatures.map(sanitizeFeature).filter(Boolean),
    ];
    const serialisedSubassemblyPayload =
      createSerialisedSubassembly && !linkCompletedDevice
        ? {
            number: serialisedSubassembly.number?.trim() || undefined,
            model: serialisedSubassembly.model?.trim() || undefined,
            color: serialisedSubassembly.color,
          }
        : undefined;
    fetch(server + "/add_manufacturing_entry", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        manufacturingRecipeId: selectedRecipe.id,
        resultingStockItemId: selectedRecipe.StockItem?.id ?? null,
        recipeStockItems: recipeStockItemsPayload,
        features: featurePayload,
        serialisedSubassembly: serialisedSubassemblyPayload,
        producedDevice: linkCompletedDevice
          ? {
              id: producedDevice?.id || undefined,
              serialNumber: producedDeviceSerialNumber,
            }
          : undefined,
        consumedSerialisedSubAssemblies: linkCompletedDevice
          ? consumedSerialisedSubAssembliesPayload
          : undefined,
        warehouseId: selectedWarehouseId,
        batchNumber: batchDetails.number.trim(),
        batchSize: submissionBatchSize,
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          if (typeof onEntryCreated === "function") {
            onEntryCreated();
          }
          dispatch(
            showPopupSnackbar(
              result.message
                ? result
                : {
                    message: t(
                      "manufacturing.entryCreated",
                      "Manufacturing entry created.",
                    ),
                    severity: "success",
                  },
            ),
          );
          hide(false);
        }
      })
      .catch(() => {
        dispatch(
          showPopupSnackbar({
            message: t(
              "manufacturing.entryCreateFailed",
              "Failed to create manufacturing entry.",
            ),
            severity: "error",
          }),
        );
      })
      .finally(() => setIsSubmitting(false));
  };

  return show ? (
    <Dialog open={show} onClose={exit} fullScreen>
      <DialogContent style={{ paddingBottom: 120 }}>
        <DialogAppBar
          header={t("manufacturing.entryHeader", "Manufacturing Entry")}
          onClose={exit}
        />
        <Box mt={8}>
          <Title>{t("manufacturing.recipes", "Manufacturing Recipes")}</Title>
          {manufacturingDataError ? (
            <Box mt={1}>
              <Typography color="error" variant="body2">
                {manufacturingDataError}
              </Typography>
            </Box>
          ) : null}
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper elevation={1}>
                <Box p={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("manufacturing.selectRecipe", "Select a recipe")}
                  </Typography>
                  <Autocomplete
                    autoHighlight
                    fullWidth
                    options={manufacturingRecipes}
                    value={selectedRecipe ?? null}
                    getOptionLabel={(option) => option?.name ?? ""}
                    getOptionSelected={(option, value) =>
                      option?.id === value?.id
                    }
                    loading={isFetchingManufacturingData}
                    loadingText={t("common.loading", "Loading…")}
                    noOptionsText={
                      isFetchingManufacturingData
                        ? t("common.loading", "Loading…")
                        : t("manufacturing.noRecipesFound", "No recipes found")
                    }
                    filterOptions={recipeFilterOptions}
                    renderOption={(option) => (
                      <Box display="flex" flexDirection="column">
                        <Typography variant="body2">{option.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {(() => {
                            const parts = [
                              option.StockItem?.sku,
                              `${componentCount(option)} ${t(
                                "manufacturing.components",
                                "components",
                              )}`,
                            ];
                            const serviceCount =
                              option.ManufacturingRecipeServiceItems?.length ??
                              0;
                            if (serviceCount) {
                              parts.push(
                                `${serviceCount} ${t(
                                  "manufacturing.services",
                                  "services",
                                )}`,
                              );
                            }
                            return parts.filter(Boolean).join(" · ");
                          })()}
                        </Typography>
                      </Box>
                    )}
                    onChange={(_, value) => {
                      setSelectedRecipeId(value?.id ?? null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        placeholder={t("common.search", "Search recipes")}
                        label={t("common.search", "Search recipes")}
                      />
                    )}
                  />
                  {hasSelectedRecipe ? (
                    <>
                      <Box mt={3}>
                        <Typography variant="subtitle2" gutterBottom>
                          {t(
                            "manufacturing.selectProductionSite",
                            "Select Production Site",
                          )}
                        </Typography>
                        <TextField
                          select
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={selectedWarehouseId}
                          disabled={!warehouseOptions.length}
                          placeholder={t(
                            "manufacturing.selectProductionSite",
                            "Select Production Site",
                          )}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (!value) {
                              setSelectedWarehouseId(null);
                              return;
                            }
                            const match = warehouseOptions.find(
                              (option) =>
                                String(option.id) === String(value ?? ""),
                            );
                            if (match) {
                              setSelectedWarehouseId(match.id);
                            } else {
                              setSelectedWarehouseId(value);
                            }
                          }}
                          helperText={
                            !warehouseOptions.length
                              ? t(
                                  "manufacturing.noWarehousesAvailable",
                                  "No warehouses available",
                                )
                              : undefined
                          }
                        >
                          {warehouseOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                      {hasSelectedWarehouse ? (
                        <Box mt={3}>
                          <Typography variant="subtitle2" gutterBottom>
                            {t("manufacturing.featureLists", "Feature lists")}
                          </Typography>
                          <Autocomplete
                            options={featureListOptions}
                            value={featureListFilter ?? null}
                            getOptionLabel={(option) => option?.name ?? ""}
                            loading={isFetchingManufacturingData}
                            loadingText={t("common.loading", "Loading…")}
                            noOptionsText={
                              isFetchingManufacturingData
                                ? t("common.loading", "Loading…")
                                : t(
                                    "manufacturing.noFeatureLists",
                                    "No feature lists found",
                                  )
                            }
                            onChange={(_, value) => {
                              setFeatureListFilter(value);
                              if (value?.features?.length) {
                                setSelectedFeatures((prev) => {
                                  const next = [...prev];
                                  value.features.forEach((feature) => {
                                    if (
                                      !next.some(
                                        (selected) =>
                                          selected.id === feature.id,
                                      )
                                    ) {
                                      next.push(feature);
                                    }
                                  });
                                  return next;
                                });
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                size="small"
                                placeholder={t(
                                  "manufacturing.searchFeatureLists",
                                  "Search feature lists",
                                )}
                                label={t(
                                  "manufacturing.searchFeatureLists",
                                  "Search feature lists",
                                )}
                              />
                            )}
                          />
                          <Box mt={3}>
                            <Typography variant="subtitle2" gutterBottom>
                              {t(
                                "manufacturing.addCustomFeature",
                                "Add custom feature",
                              )}
                            </Typography>
                            <TextField
                              variant="outlined"
                              label={t(
                                "manufacturing.featureName",
                                "Feature name",
                              )}
                              value={newFeatureName}
                              onChange={(e) =>
                                setNewFeatureName(e.target.value)
                              }
                              fullWidth
                              size="small"
                              style={{ marginBottom: 8 }}
                            />
                            <TextField
                              variant="outlined"
                              label={t(
                                "manufacturing.featureDescription",
                                "Feature description",
                              )}
                              value={newFeatureDescription}
                              onChange={(e) =>
                                setNewFeatureDescription(e.target.value)
                              }
                              fullWidth
                              multiline
                              rows={2}
                              size="small"
                            />
                            <Box
                              mt={1}
                              display="flex"
                              justifyContent="flex-end"
                            >
                              <Button
                                color="primary"
                                variant="outlined"
                                size="small"
                                disabled={!newFeatureName.trim()}
                                onClick={() => {
                                  if (!newFeatureName.trim()) {
                                    return;
                                  }
                                  setCustomFeatures((prev) => [
                                    ...prev,
                                    {
                                      id: `custom-${prev.length + 1}`,
                                      name: newFeatureName.trim(),
                                      description: newFeatureDescription.trim(),
                                    },
                                  ]);
                                  setNewFeatureName("");
                                  setNewFeatureDescription("");
                                  dispatch(
                                    showPopupSnackbar({
                                      message: t(
                                        "manufacturing.customFeatureAdded",
                                        "Custom feature added to this entry.",
                                      ),
                                      severity: "success",
                                    }),
                                  );
                                }}
                              >
                                {t("manufacturing.addFeature", "Add feature")}
                              </Button>
                            </Box>
                          </Box>
                          {selectedFeatures.length || customFeatures.length ? (
                            <Box mt={2}>
                              <Typography variant="subtitle2" gutterBottom>
                                {t(
                                  "manufacturing.selectedFeatures",
                                  "Selected features",
                                )}
                              </Typography>
                              <List dense>
                                {selectedFeatures.map((feature) => (
                                  <ListItem key={feature.id} divider>
                                    <ListItemText
                                      primary={feature.name}
                                      secondary={
                                        feature.description ||
                                        feature.featureListName?.name
                                      }
                                    />
                                    <ListItemSecondaryAction>
                                      <IconButton
                                        edge="end"
                                        aria-label={t("delete", "Delete")}
                                        onClick={() =>
                                          toggleFeatureSelection(feature)
                                        }
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </ListItemSecondaryAction>
                                  </ListItem>
                                ))}
                                {customFeatures.map((feature) => (
                                  <ListItem key={feature.id} divider>
                                    <ListItemText
                                      primary={feature.name}
                                      secondary={
                                        feature.description ||
                                        t(
                                          "manufacturing.customEntryOnly",
                                          "Custom feature for this entry",
                                        )
                                      }
                                    />
                                    <ListItemSecondaryAction>
                                      <IconButton
                                        edge="end"
                                        aria-label={t("delete", "Delete")}
                                        onClick={() =>
                                          setCustomFeatures((prev) =>
                                            prev.filter(
                                              (item) => item.id !== feature.id,
                                            ),
                                          )
                                        }
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </ListItemSecondaryAction>
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          ) : null}
                          <Box mt={2}>
                            <Grid container spacing={1} alignItems="center">
                              <Grid item xs>
                                <TextField
                                  variant="outlined"
                                  label={t(
                                    "manufacturing.batchNumber",
                                    "Batch number",
                                  )}
                                  value={batchDetails.number}
                                  onChange={(e) => {
                                    setBatchDetails({
                                      ...batchDetails,
                                      number: e.target.value,
                                    });
                                  }}
                                  fullWidth
                                  size="small"
                                  style={{ marginBottom: 8 }}
                                />
                              </Grid>
                              <Grid item>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    const randomBatch = Math.random()
                                      .toString(36)
                                      .substring(2, 10)
                                      .toUpperCase();
                                    setBatchDetails({
                                      ...batchDetails,
                                      number: randomBatch,
                                    });
                                  }}
                                >
                                  {t(
                                    "manufacturing.autoBatchNumber",
                                    "Auto batch number",
                                  )}
                                </Button>
                              </Grid>
                            </Grid>
                            <TextField
                              variant="outlined"
                              type="number"
                              label={t("manufacturing.batchSize", "Batch size")}
                              value={batchDetails.size}
                              onChange={(e) => {
                                const { value } = e.target;
                                setBatchDetails((prev) => ({
                                  ...prev,
                                  size: value,
                                }));
                              }}
                              inputProps={{ min: 1 }}
                              fullWidth
                              size="small"
                              disabled={linkCompletedDevice}
                              helperText={
                                linkCompletedDevice
                                  ? t(
                                      "manufacturing.deviceBatchSizeHint",
                                      "Device-linked production creates one device per entry.",
                                    )
                                  : undefined
                              }
                            />
                          </Box>
                          <Box mt={3}>
                            <Typography variant="subtitle2" gutterBottom>
                              {t(
                                "manufacturing.completedDevice",
                                "Completed device",
                              )}
                            </Typography>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  color="primary"
                                  checked={linkCompletedDevice}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setLinkCompletedDevice(checked);
                                    if (checked) {
                                      setCreateSerialisedSubassembly(false);
                                      setBatchDetails((prev) => ({
                                        ...prev,
                                        size: 1,
                                      }));
                                    } else {
                                      setProducedDevice(null);
                                    }
                                  }}
                                />
                              }
                              label={t(
                                "manufacturing.linkCompletedDevice",
                                "Link completed device",
                              )}
                            />
                            {linkCompletedDevice ? (
                              <Box mt={2}>
                                <Grid container spacing={2}>
                                  <DeviceSearchAutocomplete
                                    label={t(
                                      "manufacturing.producedDeviceSerial",
                                      "Produced device serial number",
                                    )}
                                    searchRestrictions={{
                                      lcState: "production",
                                      ...(selectedResultingDeviceProfileId
                                        ? {
                                            DeviceProfileId:
                                              selectedResultingDeviceProfileId,
                                          }
                                        : {}),
                                    }}
                                    additionalDataFields={[
                                      "DeviceProfile.name",
                                      "chassisNumber",
                                    ]}
                                    onChange={handleProducedDeviceChange}
                                  />
                                </Grid>
                                {producedDeviceSerialNumber ? (
                                  <Box mt={1}>
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                    >
                                      {t(
                                        "manufacturing.selectedDevice",
                                        "Selected device",
                                      )}
                                      {`: ${producedDeviceSerialNumber}`}
                                    </Typography>
                                  </Box>
                                ) : null}
                                {serialisedComponentRequirements.length ? (
                                  <Box mt={3}>
                                    <Typography
                                      variant="subtitle2"
                                      gutterBottom
                                    >
                                      {t(
                                        "manufacturing.consumedSerialisedSubAssemblies",
                                        "Consumed serialised sub-assemblies",
                                      )}
                                    </Typography>
                                    {serialisedComponentRequirements.map(
                                      (component) => {
                                        const key =
                                          getComponentSelectionKey(component);
                                        const requiredCount =
                                          getRequiredSerialisedSubAssemblyCount(
                                            component,
                                          );
                                        return Array.from({
                                          length: requiredCount,
                                        }).map((_, index) => {
                                          const currentSelection =
                                            consumedSerialisedSubAssemblies[
                                              key
                                            ]?.[index] || null;
                                          return (
                                            <Box key={`${key}-${index}`} mb={2}>
                                              <Typography
                                                variant="caption"
                                                color="textSecondary"
                                              >
                                                {[
                                                  component.StockItem?.sku,
                                                  component.StockItem?.name,
                                                  requiredCount > 1
                                                    ? `${index + 1}/${requiredCount}`
                                                    : null,
                                                ]
                                                  .filter(Boolean)
                                                  .join(" - ")}
                                              </Typography>
                                              <Grid
                                                container
                                                spacing={1}
                                                style={{ marginTop: 4 }}
                                              >
                                                <SerialisedSubAssemblySearchAutocomplete
                                                  fetchOnFocus
                                                  label={t(
                                                    "manufacturing.selectConsumedSerialisedSubAssembly",
                                                    "Select consumed SSA",
                                                  )}
                                                  searchRestrictions={{
                                                    StockItemId:
                                                      component.StockItem?.id,
                                                    availableForManufacturing: true,
                                                    ...(selectedResultingStockItemColor &&
                                                    isChassisStockItem(
                                                      component.StockItem,
                                                    )
                                                      ? {
                                                          color:
                                                            selectedResultingStockItemColor,
                                                        }
                                                      : {}),
                                                    ...(selectedWarehouseSerialisedLocation
                                                      ? {
                                                          lcLocation:
                                                            selectedWarehouseSerialisedLocation,
                                                        }
                                                      : {}),
                                                  }}
                                                  excludeIds={selectedConsumedSerialisedSubAssemblyIds.filter(
                                                    (id) =>
                                                      id !==
                                                      currentSelection?.id,
                                                  )}
                                                  onChange={(_, value) =>
                                                    updateConsumedSerialisedSubAssembly(
                                                      component,
                                                      index,
                                                      value,
                                                    )
                                                  }
                                                />
                                              </Grid>
                                            </Box>
                                          );
                                        });
                                      },
                                    )}
                                  </Box>
                                ) : null}
                              </Box>
                            ) : null}
                          </Box>
                          <Box mt={3}>
                            <Typography variant="subtitle2" gutterBottom>
                              {t(
                                "manufacturing.serialisedSubassembly",
                                "Serialised sub-assembly",
                              )}
                            </Typography>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  color="primary"
                                  checked={createSerialisedSubassembly}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setCreateSerialisedSubassembly(checked);
                                    if (checked) {
                                      setLinkCompletedDevice(false);
                                      setProducedDevice(null);
                                    }
                                  }}
                                  disabled={linkCompletedDevice}
                                />
                              }
                              label={t(
                                "manufacturing.createSerialisedSubassembly",
                                "Create serialised sub-assembly",
                              )}
                            />
                            {createSerialisedSubassembly ? (
                              <Box mt={2}>
                                <TextField
                                  variant="outlined"
                                  label={t(
                                    "serialisedSubassembly.number",
                                    "Chassis number",
                                  )}
                                  value={serialisedSubassembly.number}
                                  onChange={(e) =>
                                    setSerialisedSubassembly({
                                      ...serialisedSubassembly,
                                      number: e.target.value,
                                    })
                                  }
                                  fullWidth
                                  size="small"
                                  style={{ marginBottom: 8 }}
                                />
                                <TextField
                                  variant="outlined"
                                  label={t(
                                    "serialisedSubassembly.model",
                                    "Model",
                                  )}
                                  value={serialisedSubassembly.model}
                                  onChange={(e) =>
                                    setSerialisedSubassembly({
                                      ...serialisedSubassembly,
                                      model: e.target.value,
                                    })
                                  }
                                  fullWidth
                                  size="small"
                                  style={{ marginBottom: 8 }}
                                />
                                <FormControl
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                >
                                  <InputLabel id="serialised-color-label">
                                    {t("serialisedSubassembly.color", "Color")}
                                  </InputLabel>
                                  <Select
                                    labelId="serialised-color-label"
                                    label={t(
                                      "serialisedSubassembly.color",
                                      "Color",
                                    )}
                                    value={serialisedSubassembly.color}
                                    onChange={(e) =>
                                      setSerialisedSubassembly({
                                        ...serialisedSubassembly,
                                        color: e.target.value,
                                      })
                                    }
                                  >
                                    <MenuItem value="">
                                      <em>{t("common.notAvailable", "N/A")}</em>
                                    </MenuItem>
                                    <MenuItem value="Grey">Grey</MenuItem>
                                    <MenuItem value="White">White</MenuItem>
                                  </Select>
                                </FormControl>
                                {createSerialisedSubassembly &&
                                parsedBatchSize > 1 &&
                                hasSerialNumber ? (
                                  <Box mt={2}>
                                    <Typography
                                      variant="subtitle2"
                                      gutterBottom
                                    >
                                      {t(
                                        "manufacturing.generatedSerialNumbers",
                                        "Generated chassis numbers",
                                      )}
                                    </Typography>
                                    {serialPreview.error ? (
                                      <Typography variant="body2" color="error">
                                        {t(
                                          "manufacturing.serialNumberPreviewUnavailable",
                                          "Serial numbers cannot be generated automatically from the provided chassis number.",
                                        )}
                                      </Typography>
                                    ) : (
                                      <Paper variant="outlined">
                                        <List
                                          dense
                                          style={{
                                            maxHeight: 200,
                                            overflowY: "auto",
                                          }}
                                        >
                                          {serialPreview.numbers.map(
                                            (number) => (
                                              <ListItem key={number}>
                                                <ListItemText
                                                  primary={number}
                                                />
                                              </ListItem>
                                            ),
                                          )}
                                        </List>
                                      </Paper>
                                    )}
                                  </Box>
                                ) : null}
                              </Box>
                            ) : null}
                          </Box>
                        </Box>
                      ) : (
                        <Box mt={3}>
                          <Typography variant="body2" color="textSecondary">
                            {t(
                              "manufacturing.selectProductionSiteFirst",
                              "Select a production site to continue.",
                            )}
                          </Typography>
                        </Box>
                      )}
                    </>
                  ) : null}
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
              <Paper elevation={1}>
                <Box p={3}>
                  {selectedRecipe ? (
                    <>
                      <Typography variant="h6">
                        {selectedRecipe.name}
                      </Typography>
                      {selectedRecipe.description ? (
                        <Typography color="textSecondary" variant="body2">
                          {selectedRecipe.description}
                        </Typography>
                      ) : null}
                      <Grid container spacing={2} style={{ marginTop: 16 }}>
                        {selectedRecipe.StockItem?.imageUrl ? (
                          <Grid item xs={12} sm={3}>
                            <Box mt={1}>
                              <img
                                src={formatGoogleDriveMediaLink(
                                  selectedRecipe.StockItem.imageUrl,
                                )}
                                alt={selectedRecipe.StockItem.name}
                                style={{
                                  width: "100%",
                                  maxWidth: 200,
                                  borderRadius: 8,
                                  objectFit: "cover",
                                  border: "1px solid rgba(0,0,0,0.1)",
                                }}
                              />
                            </Box>
                          </Grid>
                        ) : null}
                        <Grid item xs={12} sm={3}>
                          <Typography variant="subtitle2" gutterBottom>
                            {t(
                              "manufacturing.resultingStockItem",
                              "Resulting stock item",
                            )}
                          </Typography>
                          <Typography variant="body1">
                            {selectedRecipe.StockItem?.name ??
                              t("common.notAvailable", "N/A")}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {selectedRecipe.StockItem?.sku}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="subtitle2" gutterBottom>
                            {t(
                              "manufacturing.serialPrefix",
                              "Serial number prefix",
                            )}
                          </Typography>
                          <Typography variant="body1">
                            {selectedRecipe.serialNumberPrefix ??
                              t("common.notAvailable", "N/A")}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="subtitle2" gutterBottom>
                            {t(
                              selectedRecipe.nextSerialNumber != null
                                ? "manufacturing.nextSerialNumber"
                                : "manufacturing.startingSerialNumber",
                              selectedRecipe.nextSerialNumber != null
                                ? "Next serial number"
                                : "Starting serial number",
                            )}
                          </Typography>
                          <Typography variant="body1">
                            {selectedRecipe.nextSerialNumber ??
                              selectedRecipe.startingSerialNumber ??
                              t("common.notAvailable", "N/A")}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Box mt={3}>
                        <Typography variant="subtitle2" gutterBottom>
                          {t(
                            "manufacturing.billOfMaterials",
                            "Bill of materials",
                          )}
                        </Typography>
                        {bomRows.length ? (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>
                                  {t("manufacturing.itemType", "Type")}
                                </TableCell>
                                <TableCell>{t("stock.sku", "SKU")}</TableCell>
                                <TableCell>{t("stock.item", "Item")}</TableCell>
                                <TableCell align="right">
                                  {t("stock.quantity", "Qty")}
                                </TableCell>
                                <TableCell align="right">
                                  {t(
                                    "manufacturing.stockProjection",
                                    "Stock projection",
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {t("manufacturing.unitCost", "Unit cost")}
                                </TableCell>
                                <TableCell align="right">
                                  {t("manufacturing.itemTotal", "Item total")}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {bomRows.map((row) => (
                                <TableRow
                                  key={row.key}
                                  hover
                                  style={
                                    row.type === "component" &&
                                    row.projectedTotal !== null &&
                                    row.projectedTotal < 0
                                      ? { backgroundColor: "lightcoral" }
                                      : undefined
                                  }
                                >
                                  <TableCell>
                                    {row.type === "component"
                                      ? t(
                                          "manufacturing.typeComponent",
                                          "Component",
                                        )
                                      : t(
                                          "manufacturing.typeService",
                                          "Service",
                                        )}
                                  </TableCell>
                                  <TableCell>
                                    {row.sku || t("common.notAvailable", "N/A")}
                                  </TableCell>
                                  <TableCell>
                                    {row.name ||
                                      t("common.notAvailable", "N/A")}
                                  </TableCell>
                                  <TableCell align="right">
                                    {row.type === "component"
                                      ? (row.quantity ??
                                        t("common.notAvailable", "N/A"))
                                      : (row.quantity ?? "—")}
                                  </TableCell>
                                  <TableCell align="right">
                                    {row.type === "component" ? (
                                      row.currentTotal !== null &&
                                      row.projectedTotal !== null ? (
                                        <Box
                                          component="span"
                                          display="inline-grid"
                                          gridTemplateColumns="minmax(0, 0.5fr) minmax(0, 0.5fr) minmax(0, 0.5fr)"
                                          alignItems="center"
                                          columnGap={0.5}
                                          width="100%"
                                        >
                                          <Box component="span">
                                            {row.currentTotal}
                                          </Box>
                                          <Box component="span">→</Box>
                                          <Box component="span">
                                            {row.projectedTotal}
                                          </Box>
                                        </Box>
                                      ) : (
                                        t("common.notAvailable", "N/A")
                                      )
                                    ) : (
                                      "—"
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    {row.cost !== null
                                      ? (formatCost(row.cost) ??
                                        t("common.notAvailable", "N/A"))
                                      : t("common.notAvailable", "N/A")}
                                  </TableCell>
                                  <TableCell align="right">
                                    {row.cost !== null
                                      ? (formatCost(
                                          row.cost *
                                            (parsedBatchSize > 0
                                              ? parsedBatchSize
                                              : 1),
                                        ) ?? t("common.notAvailable", "N/A"))
                                      : t("common.notAvailable", "N/A")}
                                  </TableCell>
                                </TableRow>
                              ))}
                              <TableRow>
                                <TableCell colSpan={5} />
                                <TableCell align="right">
                                  <Typography variant="subtitle2">
                                    {t("manufacturing.unitTotal", "Unit total")}
                                  </Typography>
                                  <Typography variant="subtitle2">
                                    {bomCostSummary.hasCost
                                      ? formatCost(bomCostSummary.perUnitTotal)
                                      : t("common.notAvailable", "N/A")}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="subtitle2">
                                    {t("common.total", "Total")}
                                  </Typography>
                                  <Typography variant="subtitle2">
                                    {bomCostSummary.hasCost
                                      ? formatCost(bomCostSummary.total)
                                      : t("common.notAvailable", "N/A")}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            {t(
                              "manufacturing.noBomItems",
                              "No bill of materials recorded.",
                            )}
                          </Typography>
                        )}
                        {hasInvalidQuantities ? (
                          <Box mt={1}>
                            <Typography color="error" variant="caption">
                              {t(
                                "manufacturing.invalidQuantities",
                                "All component quantities must be greater than zero.",
                              )}
                            </Typography>
                          </Box>
                        ) : null}
                      </Box>
                    </>
                  ) : (
                    <Typography color="textSecondary">
                      {t(
                        "manufacturing.noRecipeSelected",
                        "Select a recipe from the left to view its details.",
                      )}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
        <Box height={100} />
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            padding: 16,
            backgroundColor: "white",
            boxShadow: "0px -4px 12px rgba(0, 0, 0, 0.1)",
            zIndex: 10,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Button
                color="primary"
                variant="contained"
                disabled={disableSubmit}
                onClick={handleSubmit}
              >
                {isSubmitting
                  ? t("common.saving", "Saving…")
                  : t("manufacturing.useRecipe", "Use this recipe")}
              </Button>
            </Grid>
            <Grid item>
              <Button variant="outlined" onClick={exit}>
                {t("cancel", "Cancel")}
              </Button>
            </Grid>
          </Grid>
        </div>
      </DialogContent>
    </Dialog>
  ) : null;
}
